import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { api, formatApiError } from "@/lib/api";
import { ImageUploader } from "./ImageUploader";
import { STYLES } from "@/components/FilterRow";

export const inputCls = "w-full h-11 bg-[#0D0D11] border border-[#D4AF37]/20 px-3 text-sm text-[#F5F3EF] focus:outline-none focus:border-[#D4AF37]";
const labelCls = "block font-mono-j text-[11px] uppercase tracking-[0.18em] text-[#A09E97] mb-1.5";
const EMPTY = { name: "", category: "", supplier: "", code: "", style: "", description: "", images: [], specs: [], featured: false, order: 0 };

export const ItemForm = ({ open, onClose, item, cats, onSaved }) => {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setF(item ? { ...EMPTY, ...item } : { ...EMPTY, category: cats[0]?.slug || "" });
  }, [open, item, cats]);

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const setSpec = (i, k, v) => set("specs", f.specs.map((s, j) => (j === i ? { ...s, [k]: v } : s)));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const body = { ...f, specs: f.specs.filter((s) => s.label.trim()) };
    delete body.id; delete body.created_at;
    try {
      if (item) await api.put(`/items/${item.id}`, body);
      else await api.post("/items", body);
      toast.success(item ? "Item updated" : "Item added");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-lenis-prevent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-[#13141C] border-[#D4AF37]/25 text-[#F5F3EF] rounded-none" data-testid="item-form-dialog">
        <DialogHeader><DialogTitle className="font-display text-3xl font-normal">{item ? "Edit item" : "New item"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-5">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className={labelCls}>Name</label><input required value={f.name} onChange={(e) => set("name", e.target.value)} className={inputCls} data-testid="item-form-name" /></div>
            <div><label className={labelCls}>Category</label>
              <Select value={f.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="h-11 rounded-none bg-[#0D0D11] border-[#D4AF37]/20" data-testid="item-form-category"><SelectValue placeholder="Choose" /></SelectTrigger>
                <SelectContent className="bg-[#13141C] border-[#D4AF37]/30 text-[#F5F3EF]">
                  {cats.map((c) => <SelectItem key={c.id} value={c.slug}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><label className={labelCls}>Supplier</label><input value={f.supplier} onChange={(e) => set("supplier", e.target.value)} className={inputCls} data-testid="item-form-supplier" /></div>
            <div><label className={labelCls}>Code / SKU</label><input value={f.code} onChange={(e) => set("code", e.target.value)} className={inputCls} data-testid="item-form-code" /></div>
            <div><label className={labelCls}>Style / type</label>
              <input list="style-options" value={f.style} onChange={(e) => set("style", e.target.value)} placeholder="e.g. Woodgrain, Knobs" className={inputCls} data-testid="item-form-style" />
              <datalist id="style-options">{STYLES.map((s) => <option key={s} value={s} />)}</datalist>
            </div>
          </div>
          <div><label className={labelCls}>Description</label>
            <textarea rows={3} value={f.description} onChange={(e) => set("description", e.target.value)} className={`${inputCls} h-auto py-2`} data-testid="item-form-description" />
          </div>
          <div><label className={labelCls}>Photos (first is cover)</label><ImageUploader images={f.images} onChange={(v) => set("images", v)} testid="item-form-images" /></div>
          <div>
            <label className={labelCls}>Specifications</label>
            {f.specs.map((s, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input placeholder="Label" value={s.label} onChange={(e) => setSpec(i, "label", e.target.value)} className={inputCls} data-testid={`item-form-spec-label-${i}`} />
                <input placeholder="Value" value={s.value} onChange={(e) => setSpec(i, "value", e.target.value)} className={inputCls} data-testid={`item-form-spec-value-${i}`} />
                <button type="button" onClick={() => set("specs", f.specs.filter((_, j) => j !== i))} className="px-3 text-[#A09E97] hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            <button type="button" onClick={() => set("specs", [...f.specs, { label: "", value: "" }])} data-testid="item-form-add-spec"
              className="inline-flex items-center gap-1 text-xs text-[#E5C158]"><Plus className="w-3.5 h-3.5" /> Add spec</button>
          </div>
          <label className="flex items-center gap-3 text-sm">
            <Switch checked={f.featured} onCheckedChange={(v) => set("featured", v)} data-testid="item-form-featured" />
            Feature on home page
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="h-11 px-6 rounded-full border border-[#D4AF37]/30 text-sm">Cancel</button>
            <button disabled={busy || !f.category} data-testid="item-form-save" className="h-11 px-8 rounded-full bg-[#D4AF37] text-[#0D0D11] text-sm font-medium disabled:opacity-60">
              {busy ? "Saving…" : "Save item"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
